import React from 'react';
import { X } from 'lucide-react';

const FarmRecordDetails = ({ record, onClose }) => {
  if (!record) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatNumber = (number) => {
    return parseFloat(number || 0).toFixed(2);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Farm Record Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              Record from {formatDate(record.date)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Farmer</label>
                <p className="text-sm text-gray-900 mt-1">{record.farmerName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Flop</label>
                <p className="text-sm text-gray-900 mt-1">{record.flopName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(record.date)}</p>
              </div>
            </div>
          </div>

          {/* Manual Entry Data */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-3">Manual Entry Data</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-700">Tray</label>
                <p className="text-sm text-blue-900 mt-1">{formatNumber(record.tray)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">Delivered Tray</label>
                <p className="text-sm text-blue-900 mt-1">{formatNumber(record.deliveredTray)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">Given & Crack Tray</label>
                <p className="text-sm text-blue-900 mt-1">{formatNumber(record.givenAndCrackTray)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">New Bag</label>
                <p className="text-sm text-blue-900 mt-1">{formatNumber(record.newBag)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">Per Day Bag</label>
                <p className="text-sm text-blue-900 mt-1">{formatNumber(record.perDayBag)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">Given Bag</label>
                <p className="text-sm text-blue-900 mt-1">{formatNumber(record.givenBag)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">Mortality</label>
                <p className="text-sm text-blue-900 mt-1">{formatNumber(record.mortality)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">Medicine</label>
                <p className="text-sm text-blue-900 mt-1">{record.medicine || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">Vaccine</label>
                <p className="text-sm text-blue-900 mt-1">{record.vaccine || '-'}</p>
              </div>
            </div>
          </div>

          {/* Auto Calculated Data */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-900 mb-3">Auto Calculated Data</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-green-700">Tray Stock</label>
                <p className="text-sm font-medium text-green-900 mt-1">{formatNumber(record.trayStock)}</p>
                <p className="text-xs text-green-600 mt-1">Last Tray + Tray - Delivered Tray - Given & Crack Tray</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-green-700">Bag Stock</label>
                <p className="text-sm font-medium text-green-900 mt-1">{formatNumber(record.bagStock)}</p>
                <p className="text-xs text-green-600 mt-1">Last Bag Stock + New Bag - Per Day Bag - Given Bag</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-green-700">Production Percentage</label>
                <p className="text-sm font-medium text-green-900 mt-1">{formatNumber(record.productionPercentage)}%</p>
                <p className="text-xs text-green-600 mt-1">(Tray × 30) × 100 / Live Bird</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-green-700">Feed Consumption</label>
                <p className="text-sm font-medium text-green-900 mt-1">{formatNumber(record.feedConsumption)}</p>
                <p className="text-xs text-green-600 mt-1">(Given Bag × 50) × 1000 / Live Bird</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-green-700">Live Bird</label>
                <p className="text-sm font-medium text-green-900 mt-1">{formatNumber(record.liveBird)}</p>
                <p className="text-xs text-green-600 mt-1">Farmer's Live Birds - Mortality</p>
              </div>
                             <div>
                 <label className="block text-sm font-medium text-green-700">Bird Days</label>
                 <p className="text-sm font-medium text-green-900 mt-1">{formatNumber(record.birdDays)}</p>
                 <p className="text-xs text-green-600 mt-1">Days from bird date to record date</p>
               </div>
              {record.birdAge && (
                                 <div>
                   <label className="block text-sm font-medium text-green-700">Bird Age</label>
                   <p className="text-sm font-medium text-green-900 mt-1">{record.birdAge}</p>
                   <p className="text-xs text-green-600 mt-1">Days from bird date to record date</p>
                 </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Record Metadata</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Created At</label>
                <p className="text-sm text-gray-900 mt-1">
                  {record.createdAt ? new Date(record.createdAt.toDate()).toLocaleString() : '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                <p className="text-sm text-gray-900 mt-1">
                  {record.updatedAt ? new Date(record.updatedAt.toDate()).toLocaleString() : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FarmRecordDetails; 